import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import { getRoles, getRole, createRole, updateRole, deleteRole, getPermissions } from './api';
import { Role, PermissionGroup } from '../../shared/types';
import { extractErrorMessage } from '../../shared/utils/errors';

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [rolesData, permsData] = await Promise.all([getRoles(), getPermissions()]);
      setRoles(rolesData);
      setPermissions(permsData);
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      await createRole({ ...formData, permissions: selectedPermissions });
      setIsCreateModalOpen(false);
      setFormData({ name: '', description: '' });
      setSelectedPermissions([]);
      loadData();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to create role'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) return;
    setFormError('');
    setIsSubmitting(true);
    try {
      await updateRole(selectedRole.id, { ...formData, permissions: selectedPermissions });
      setIsEditModalOpen(false);
      setSelectedRole(null);
      loadData();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to update role'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    setFormError('');
    setIsSubmitting(true);
    try {
      await deleteRole(selectedRole.id);
      setIsDeleteModalOpen(false);
      setSelectedRole(null);
      loadData();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to delete role'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = async (role: Role) => {
    setSelectedRole(role);
    setFormData({ name: role.name, description: role.description || '' });
    setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    setFormError('');
    setIsEditModalOpen(true);
    setIsLoadingRole(true);
    try {
      const full = await getRole(role.id);
      setSelectedRole(full);
      setFormData({ name: full.name, description: full.description || '' });
      setSelectedPermissions(full.permissions?.map((p) => p.id) || []);
    } catch (e) {
      console.error('Failed to load role details:', e);
    } finally {
      setIsLoadingRole(false);
    }
  };

  const openPermissionsModal = async (role: Role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    setFormError('');
    setIsPermissionsModalOpen(true);
    setIsLoadingRole(true);
    try {
      const full = await getRole(role.id);
      setSelectedRole(full);
      setSelectedPermissions(full.permissions?.map((p) => p.id) || []);
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to load role permissions'));
    } finally {
      setIsLoadingRole(false);
    }
  };

  const openDeleteModal = (role: Role) => {
    setSelectedRole(role);
    setFormError('');
    setIsDeleteModalOpen(true);
  };

  const togglePermission = (permId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const toggleGroup = (group: PermissionGroup) => {
    const groupIds = group.permissions.map((p) => p.id);
    const allSelected = groupIds.every((id) => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      setSelectedPermissions((prev) => Array.from(new Set(prev.concat(groupIds))));
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setFormError('');
    setIsSubmitting(true);
    try {
      await updateRole(selectedRole.id, { permissions: selectedPermissions });
      setIsPermissionsModalOpen(false);
      setSelectedRole(null);
      loadData();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to update permissions'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const PermissionSelector: React.FC = () => (
    <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
      {permissions.map((group) => {
        const groupIds = group.permissions.map((p) => p.id);
        const allSelected = groupIds.every((id) => selectedPermissions.includes(id));
        const someSelected = groupIds.some((id) => selectedPermissions.includes(id));

        return (
          <div key={group.group} className="border border-slate-700/50 rounded-lg p-3">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={() => toggleGroup(group)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30"
              />
              <span className="text-sm font-semibold text-white">{group.group}</span>
              <span className="text-xs text-slate-500">
                ({groupIds.filter((id) => selectedPermissions.includes(id)).length}/{groupIds.length})
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 ml-6">
              {group.permissions.map((perm) => (
                <label key={perm.id} className="flex items-center gap-2 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                    className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30"
                  />
                  <span className="text-xs text-slate-300">{perm.name}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Roles & Permissions</h1>
            <p className="text-slate-400 mt-1">Manage access control roles</p>
          </div>
          <button
            onClick={() => {
              setFormData({ name: '', description: '' });
              setSelectedPermissions([]);
              setFormError('');
              setIsCreateModalOpen(true);
            }}
            className="btn btn-primary"
          >
            + Create Role
          </button>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">Role</th>
                    <th className="table-header">Description</th>
                    <th className="table-header">Permissions</th>
                    <th className="table-header">Members</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {roles.map((role) => (
                    <tr key={role.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{role.name}</span>
                          {role.is_system && (
                            <span className="badge badge-info text-[10px]">System</span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="text-slate-400 text-sm">{role.description || '—'}</span>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-warning">{role.permissions_count ?? 0}</span>
                      </td>
                      <td className="table-cell">
                        <span className="text-slate-300">{role.admins_count ?? 0}</span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPermissionsModal(role)}
                            className="px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-all"
                          >
                            Permissions
                          </button>
                          {!role.is_system && (
                            <>
                              <button
                                onClick={() => openEditModal(role)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => openDeleteModal(role)}
                                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Create New Role">
        <form onSubmit={handleCreate} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
              placeholder="e.g. Support Manager"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows={2}
              placeholder="Describe this role's purpose"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Permissions</label>
            <PermissionSelector />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={() => setIsCreateModalOpen(false)} className="flex-1 btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 btn btn-primary disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Role">
        <form onSubmit={handleEdit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
              Permissions
              {isLoadingRole && (
                <span className="inline-block w-3 h-3 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              )}
            </label>
            <PermissionSelector />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={() => setIsEditModalOpen(false)} className="flex-1 btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || isLoadingRole} className="flex-1 btn btn-primary disabled:opacity-50">
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Permissions Modal */}
      <Modal isOpen={isPermissionsModalOpen} onClose={() => setIsPermissionsModalOpen(false)} title={`Permissions: ${selectedRole?.name}`}>
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}
          {selectedRole?.is_system && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-sm">
              This is a system role — its permissions are fixed and cannot be modified. You can view them here for reference.
            </div>
          )}
          {isLoadingRole && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="inline-block w-3 h-3 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Loading current permissions...
            </div>
          )}
          <PermissionSelector />
          <div className="flex gap-3 pt-4">
            <button onClick={() => setIsPermissionsModalOpen(false)} className="flex-1 btn btn-secondary">
              {selectedRole?.is_system ? 'Close' : 'Cancel'}
            </button>
            {!selectedRole?.is_system && (
              <button
                onClick={handleSavePermissions}
                disabled={isSubmitting || isLoadingRole}
                className="flex-1 btn btn-primary disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save Permissions'}
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Role">
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}
          <p className="text-slate-300">
            Are you sure you want to delete <strong className="text-white">{selectedRole?.name}</strong>?
            All admins assigned to this role will lose these permissions.
          </p>
          <div className="flex gap-3 pt-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="flex-1 btn btn-secondary">
              Cancel
            </button>
            <button onClick={handleDelete} disabled={isSubmitting} className="flex-1 btn btn-danger disabled:opacity-50">
              {isSubmitting ? 'Deleting...' : 'Delete Role'}
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default RolesPage;
